import { Form, FormInstance, Input, Select } from 'antd';
import React from 'react';
import { TwilioBusinessType } from '../../types/twilio-onboarding';
import {
  getBusinessNameLengthError,
  getLegalEntityNameError,
  TWILIO_CUSTOMER_NAME_MAX_LENGTH,
  urlValidator,
} from './onboarding-logic';

const BUSINESS_TYPES: TwilioBusinessType[] = [
  'Co-operative',
  'Corporation',
  'Government',
  'Limited Liability Corporation',
  'Non-profit Corporation',
  'Partnership',
  'Private Corporation',
  'Public Corporation',
  'Sole Proprietorship',
];

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
];

interface BusinessInfoFormProps {
  form: FormInstance;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const BusinessInfoForm: React.FC<BusinessInfoFormProps> = ({ form }) => {
  return (
    <>
      <Form.Item
        dependencies={[['business_info', 'brand_name']]}
        label="Legal Entity Name"
        name={['business_info', 'legal_entity_name']}
        rules={[
          { required: true, message: 'Legal entity name is required' },
          { min: 2 },
          {
            validator: (_, value) => {
              const error = getLegalEntityNameError(value);
              return error
                ? Promise.reject(new Error(error))
                : Promise.resolve();
            },
          },
          ({ getFieldValue }) => ({
            validator: (_, value) => {
              const error = getBusinessNameLengthError(
                value,
                getFieldValue(['business_info', 'brand_name']),
              );
              return error
                ? Promise.reject(new Error(error))
                : Promise.resolve();
            },
          }),
        ]}
      >
        <Input
          maxLength={TWILIO_CUSTOMER_NAME_MAX_LENGTH}
          placeholder="e.g. Acme Realty LLC"
        />
      </Form.Item>

      <Form.Item
        dependencies={[['business_info', 'legal_entity_name']]}
        label="Brand Name"
        name={['business_info', 'brand_name']}
        rules={[
          { required: true, message: 'Brand name is required' },
          { min: 2 },
          ({ getFieldValue }) => ({
            validator: (_, value) => {
              const error = getBusinessNameLengthError(
                getFieldValue(['business_info', 'legal_entity_name']),
                value,
              );
              return error
                ? Promise.reject(new Error(error))
                : Promise.resolve();
            },
          }),
        ]}
      >
        <Input
          maxLength={TWILIO_CUSTOMER_NAME_MAX_LENGTH}
          placeholder="e.g. Acme Realty"
        />
      </Form.Item>

      <Form.Item
        label="Business Type"
        name={['business_info', 'business_type']}
        rules={[{ required: true, message: 'Business type is required' }]}
      >
        <Select placeholder="Select business type">
          {BUSINESS_TYPES.map(t => (
            <Select.Option key={t} value={t}>
              {t}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="EIN"
        name={['business_info', 'ein']}
        rules={[
          { required: true, message: 'EIN is required' },
          {
            pattern: /^\d{2}-\d{7}$/,
            message: 'EIN must be in XX-XXXXXXX format',
          },
        ]}
      >
        <Input placeholder="12-3456789" />
      </Form.Item>

      <Form.Item
        label="Business Website"
        name={['business_info', 'business_website']}
        rules={[
          { required: true, message: 'Business website is required' },
          { validator: urlValidator },
        ]}
      >
        <Input placeholder="https://www.example.com" />
      </Form.Item>

      <Form.Item
        label="Street Address"
        name={['business_info', 'street_address']}
        rules={[{ required: true, message: 'Street address is required' }]}
      >
        <Input placeholder="123 Main St" />
      </Form.Item>

      <Form.Item
        label="Street Address 2"
        name={['business_info', 'street_address_2']}
      >
        <Input placeholder="Suite 100 (optional)" />
      </Form.Item>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 120px 120px',
          gap: 16,
        }}
      >
        <Form.Item
          label="City"
          name={['business_info', 'city']}
          rules={[{ required: true, message: 'City is required' }]}
        >
          <Input placeholder="e.g. Miami" />
        </Form.Item>

        <Form.Item
          label="State"
          name={['business_info', 'state']}
          rules={[{ required: true, message: 'State is required' }]}
        >
          <Select placeholder="State" showSearch>
            {US_STATES.map(s => (
              <Select.Option key={s} value={s}>
                {s}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="ZIP Code"
          name={['business_info', 'postal_code']}
          rules={[
            { required: true, message: 'ZIP is required' },
            {
              pattern: /^\d{5}(-\d{4})?$/,
              message: 'Must be 5-digit or ZIP+4',
            },
          ]}
        >
          <Input placeholder="e.g. 33101" />
        </Form.Item>
      </div>
    </>
  );
};
